import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Calendar, 
  Clock, 
  MapPin, 
  User, 
  Play, 
  Edit, 
  Trash2,
  Eye,
  FileText,
  MoreHorizontal
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { format } from 'date-fns';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useRole } from '@/hooks/useRole';
import { toast } from 'sonner';
import { MeetingScheduler } from './MeetingScheduler';
import { KPIRecordButton } from './KPIValueRecorder';

interface Meeting {
  id: string;
  title: string;
  meeting_type: string;
  status: string;
  scheduled_date: string;
  actual_start_time?: string;
  actual_end_time?: string;
  duration_minutes?: number;
  location?: string;
  description?: string;
  observer_id: string;
  observee_id: string;
  observer_profile?: {
    first_name: string;
    last_name: string;
  };
  observee_profile?: {
    first_name: string;
    last_name: string;
  };
}

interface MeetingListProps {
  onStartMeeting: (meeting: Meeting) => void;
  onRefresh: () => void;
  canSchedule: boolean;
}

export function MeetingList({ onStartMeeting, onRefresh, canSchedule }: MeetingListProps) {
  const { user } = useAuth();
  const { isAdmin } = useRole();
  const [meetings, setMeetings] = useState<Meeting[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingMeeting, setEditingMeeting] = useState<Meeting | null>(null);

  useEffect(() => {
    loadMeetings();
  }, [user]);

  const loadMeetings = async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('meetings')
        .select('*')
        .or(`observer_id.eq.${user.id},observee_id.eq.${user.id}`)
        .order('scheduled_date', { ascending: true });

      if (error) throw error;

      // Load profile data separately
      const userIds = Array.from(new Set([
        ...data?.map(m => m.observer_id) || [],
        ...data?.map(m => m.observee_id) || []
      ]));

      const { data: profiles, error: profilesError } = await supabase
        .from('profiles')
        .select('id, first_name, last_name')
        .in('id', userIds);

      if (profilesError) throw profilesError;

      const profileMap = profiles?.reduce((acc, profile) => {
        acc[profile.id] = profile;
        return acc;
      }, {} as Record<string, any>) || {};

      const meetingsWithProfiles = data?.map(meeting => ({
        ...meeting,
        observer_profile: profileMap[meeting.observer_id],
        observee_profile: profileMap[meeting.observee_id]
      })) || [];

      setMeetings(meetingsWithProfiles);
    } catch (error) {
      console.error('Error loading meetings:', error);
      toast.error('Failed to load meetings');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (meeting: Meeting) => {
    if (!confirm(`Are you sure you want to delete "${meeting.title}"?`)) return;

    try {
      const { error } = await supabase
        .from('meetings')
        .delete()
        .eq('id', meeting.id);

      if (error) throw error;
      toast.success('Meeting deleted');
      loadMeetings();
      onRefresh();
    } catch (error) {
      console.error('Error deleting meeting:', error);
      toast.error('Failed to delete meeting');
    }
  };

  const handleStatusChange = async (meeting: Meeting, newStatus: string) => {
    try {
      const updates: any = { status: newStatus };
      
      if (newStatus === 'in_progress' && !meeting.actual_start_time) {
        updates.actual_start_time = new Date().toISOString();
      }

      const { error } = await supabase
        .from('meetings')
        .update(updates)
        .eq('id', meeting.id);

      if (error) throw error;
      
      toast.success(`Meeting marked as ${newStatus.replace('_', ' ')}`);
      loadMeetings();
      onRefresh();
    } catch (error) {
      console.error('Error updating meeting status:', error);
      toast.error('Failed to update meeting status');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'scheduled': return 'bg-blue-100 text-blue-800';
      case 'in_progress': return 'bg-yellow-100 text-yellow-800';
      case 'completed': return 'bg-green-100 text-green-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getMeetingTypeColor = (type: string) => {
    switch (type) {
      case 'observation': return 'bg-purple-100 text-purple-800';
      case 'mentoring': return 'bg-green-100 text-green-800';
      case 'coaching': return 'bg-blue-100 text-blue-800';
      case 'evaluation': return 'bg-orange-100 text-orange-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDuration = (minutes?: number) => {
    if (!minutes) return '';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
  };

  const canEditMeeting = (meeting: Meeting) => {
    return isAdmin || meeting.observer_id === user?.id;
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="py-8">
          <div className="text-center text-muted-foreground">Loading meetings...</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <div className="space-y-4">
        {meetings.map((meeting) => (
          <Card key={meeting.id} className="w-full">
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between">
                <div className="space-y-1">
                  <CardTitle className="text-lg">{meeting.title}</CardTitle>
                  <div className="flex items-center gap-4 text-sm text-muted-foreground">
                    <div className="flex items-center gap-1">
                      <Calendar className="h-4 w-4" />
                      {format(new Date(meeting.scheduled_date), 'PPP')}
                    </div>
                    <div className="flex items-center gap-1">
                      <Clock className="h-4 w-4" />
                      {format(new Date(meeting.scheduled_date), 'HH:mm')}
                    </div>
                    {meeting.location && (
                      <div className="flex items-center gap-1">
                        <MapPin className="h-4 w-4" />
                        {meeting.location}
                      </div>
                    )}
                  </div>
                </div>
                
                <div className="flex items-center gap-2">
                  <Badge className={getStatusColor(meeting.status)}>
                    {meeting.status.replace('_', ' ')}
                  </Badge>
                  <Badge variant="outline" className={getMeetingTypeColor(meeting.meeting_type)}>
                    {meeting.meeting_type}
                  </Badge>
                </div>
              </div>
            </CardHeader>

            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4 text-sm">
                  <div className="flex items-center gap-1">
                    <Eye className="h-4 w-4" />
                    <span>Observer: {meeting.observer_profile?.first_name} {meeting.observer_profile?.last_name}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <User className="h-4 w-4" />
                    <span>Participant: {meeting.observee_profile?.first_name} {meeting.observee_profile?.last_name}</span>
                  </div>
                  {meeting.duration_minutes && (
                    <div className="flex items-center gap-1">
                      <Clock className="h-4 w-4" />
                      <span>Duration: {formatDuration(meeting.duration_minutes)}</span>
                    </div>
                  )}
                </div>

                <div className="flex items-center gap-2">
                  {meeting.status === 'scheduled' && meeting.observer_id === user?.id && (
                    <Button
                      size="sm"
                      onClick={() => onStartMeeting(meeting)}
                      className="flex items-center gap-1"
                    >
                      <Play className="h-4 w-4" />
                      Start
                    </Button>
                  )}
                  
                  {meeting.status === 'in_progress' && meeting.observer_id === user?.id && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => onStartMeeting(meeting)}
                      className="flex items-center gap-1"
                    >
                      <Clock className="h-4 w-4" />
                      Continue
                    </Button>
                  )}
                  
                  {meeting.status === 'completed' && (
                    <KPIRecordButton meetingId={meeting.id} />
                  )}

                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm">
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      {canEditMeeting(meeting) && meeting.status === 'scheduled' && (
                        <DropdownMenuItem onClick={() => setEditingMeeting(meeting)}>
                          <Edit className="h-4 w-4 mr-2" />
                          Edit
                        </DropdownMenuItem>
                      )}
                      
                      {meeting.status === 'scheduled' && meeting.observer_id === user?.id && (
                        <DropdownMenuItem onClick={() => handleStatusChange(meeting, 'cancelled')}>
                          <Trash2 className="h-4 w-4 mr-2" />
                          Cancel
                        </DropdownMenuItem>
                      )}
                      
                      {meeting.status === 'completed' && (
                        <DropdownMenuItem>
                          <FileText className="h-4 w-4 mr-2" />
                          View Notes
                        </DropdownMenuItem>
                      )}
                      
                      {canEditMeeting(meeting) && (
                        <DropdownMenuItem 
                          onClick={() => handleDelete(meeting)}
                          className="text-destructive"
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Delete
                        </DropdownMenuItem>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>

              {meeting.description && (
                <p className="text-sm text-muted-foreground">{meeting.description}</p>
              )}
            </CardContent>
          </Card>
        ))}

        {meetings.length === 0 && (
          <Card>
            <CardContent className="py-8 text-center">
              <Calendar className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold">No meetings scheduled</h3>
              <p className="text-muted-foreground mb-6">
                {canSchedule 
                  ? 'Schedule your first observation or mentoring meeting.'
                  : 'No meetings have been scheduled for you yet.'
                }
              </p>
            </CardContent>
          </Card>
        )}
      </div>

      {editingMeeting && (
        <MeetingScheduler
          editMeeting={editingMeeting}
          onClose={() => setEditingMeeting(null)}
          onScheduled={() => {
            setEditingMeeting(null);
            loadMeetings();
            onRefresh();
          }}
        />
      )}
    </>
  );
}